// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
// SLIK shared brain — RECEIPTS (Jose 2026-08-18).
// The ONE definition of "a receipt" so the POS Receipts tab, the client-profile history, and any
// barber-finance view all agree on the total. In production a receipt = a COMPLETED checkout, which is
// stored as a completed appointment record: service price − discount + products sold + tip + emergency
// fee. This matches how reportingDataService credits revenue, so Receipts never disagrees with Reports.
// Pure logic — no Firebase/React (structural typing so it doesn't import the Appointment type).

export interface ReceiptProduct {
  productName?: string;
  quantity?: number;
  unitPrice?: number;
  total?: number;
  barberId?: string;
  barberName?: string;
}

export interface ReceiptAppt {
  id: string;
  clientName?: string;
  clientId?: string;
  barberId?: string;
  barberName?: string;
  service?: string;
  date?: string;   // YYYY-MM-DD
  time?: string;   // HH:MM
  price?: number;  // service price (original)
  status?: string;
  appointmentStatus?: string; // legacy: 'checked_out'
  tipAmount?: number;
  discountAmount?: number;
  discountReason?: string;    // the named reason/label the discount was applied under
  emergencyFee?: number;
  paymentMethod?: string;
  completedAt?: any; // Date | string | Firestore Timestamp
  taxAmount?: number;  // tax actually charged at checkout (products tax; services untaxed in NJ)
  ticketTotal?: number; // checkout's stored grand total — when present it IS the amount paid
  productsSold?: ReceiptProduct[];
  stripePaymentIntentId?: string; // present → the sale can be refunded to the card via Stripe
  paidOnline?: boolean;
}

export interface ReceiptLine {
  label: string;
  qty: number;
  amount: number; // line total
  kind: 'service' | 'product';
}

export interface Receipt {
  id: string;
  clientName: string;
  barberName: string;
  barberId: string;
  service: string;
  date: string;
  time: string;
  when: number; // sortable ms timestamp (completedAt, else date+time)
  lines: ReceiptLine[];
  serviceSubtotal: number; // service price net of discount
  productsSubtotal: number;
  discount: number;
  discountReason: string;  // named reason/label (e.g. "Complimentary", "Loyalty") — '' if none
  tip: number;
  emergencyFee: number;
  tax: number;   // tax actually charged (0 when none stored)
  total: number; // the full amount the client paid
  paymentMethod: string;
  paymentLabel: string;
  stripePaymentIntentId?: string; // present → refundable to card via Stripe (else void locally)
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** A completed/paid/checked-out ticket is a receipt — matches reportingDataService's revenue filter. */
export function isReceiptAppt(a: ReceiptAppt): boolean {
  return a.status === 'completed' || a.status === 'paid' || a.appointmentStatus === 'checked_out';
}

export function paymentMethodLabel(method?: string): string {
  switch ((method || '').toLowerCase()) {
    case 'cash': return 'Cash';
    case 'card': return 'Card';
    case 'mobile':
    case 'mobile_link': return 'Mobile';
    case 'gift_card': return 'Gift Card';
    case 'store_credit': return 'Store Credit';
    // Honest, never a bare dash — imported Fresha rows have no method; "Unknown" says so.
    default: return method || 'Unknown';
  }
}

/** Products subtotal for an appointment (each product's stored total, else qty × unitPrice). */
export function receiptProductsSubtotal(appt: ReceiptAppt): number {
  return round2((appt.productsSold || []).reduce((s, p) =>
    s + (typeof p.total === 'number' ? p.total : (p.quantity || 1) * (p.unitPrice || 0)), 0));
}

/** The full amount the client paid — checkout's stored ticketTotal when present (it already
 *  includes tax), else service − discount + products + tip + emergency fee + tax.
 *  (Restored 2026-09-20 from the babel cache after an unsynced src/shared edit was clobbered —
 *  edit THIS root file only, then scripts/sync-shared.sh.) */
export function receiptTotal(appt: ReceiptAppt): number {
  const stored = appt.ticketTotal;
  if (typeof stored === 'number' && stored > 0) return round2(stored);
  const service = appt.price || 0;
  const discount = appt.discountAmount || 0;
  const products = receiptProductsSubtotal(appt);
  const tip = appt.tipAmount || 0;
  const emergencyFee = appt.emergencyFee || 0;
  const tax = appt.taxAmount || 0;
  return round2(service - discount + products + tip + emergencyFee + tax);
}

/** Sortable timestamp — prefer completedAt (Date | string | Firestore Timestamp), else date+time. */
export function receiptWhen(appt: ReceiptAppt): number {
  const c: any = appt.completedAt;
  if (c) {
    if (typeof c?.toDate === 'function') return c.toDate().getTime();
    if (c instanceof Date) return c.getTime();
    const t = new Date(c).getTime();
    if (!isNaN(t)) return t;
  }
  // Build from date + time at LOCAL time. appt.time may be 12-hour ("5:30 PM") or 24-hour ("17:30").
  // The old `new Date(`${date}T${time}`)` went Invalid on every 12-hour time → NaN → 0, which sorted
  // all such receipts to epoch 1970 and scrambled receipt order. Parse defensively. (Jose 2026-10-23)
  const [yy, mm, dd] = (appt.date || '1970-01-01').split('-').map((n) => parseInt(n, 10));
  if (!yy || !mm || !dd) return 0;
  let hh = 0;
  let mi = 0;
  const tm = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(appt.time || '');
  if (tm) {
    hh = parseInt(tm[1], 10);
    mi = parseInt(tm[2], 10);
    const ap = (tm[3] || '').toUpperCase();
    if (ap === 'PM' && hh < 12) hh += 12;
    if (ap === 'AM' && hh === 12) hh = 0;
  }
  return new Date(yy, mm - 1, dd, hh, mi, 0, 0).getTime();
}

/** Normalize a completed appointment into a full Receipt (line items + totals). */
export function buildReceipt(appt: ReceiptAppt): Receipt {
  const lines: ReceiptLine[] = [];
  if ((appt.service || '').trim() || (appt.price || 0) > 0) {
    lines.push({ label: appt.service || 'Service', qty: 1, amount: round2(appt.price || 0), kind: 'service' });
  }
  (appt.productsSold || []).forEach(p => {
    lines.push({
      label: p.productName || 'Product',
      qty: p.quantity || 1,
      amount: round2(typeof p.total === 'number' ? p.total : (p.quantity || 1) * (p.unitPrice || 0)),
      kind: 'product',
    });
  });
  return {
    id: appt.id,
    clientName: appt.clientName || 'Walk-in',
    barberName: appt.barberName || '',
    barberId: appt.barberId || '',
    service: appt.service || '',
    date: appt.date || '',
    time: appt.time || '',
    when: receiptWhen(appt),
    lines,
    serviceSubtotal: round2((appt.price || 0) - (appt.discountAmount || 0)),
    productsSubtotal: receiptProductsSubtotal(appt),
    discount: round2(appt.discountAmount || 0),
    discountReason: appt.discountReason || '',
    tip: round2(appt.tipAmount || 0),
    emergencyFee: round2(appt.emergencyFee || 0),
    tax: round2(appt.taxAmount || 0),
    total: receiptTotal(appt),
    paymentMethod: appt.paymentMethod || '',
    paymentLabel: paymentMethodLabel(appt.paymentMethod),
    stripePaymentIntentId: appt.stripePaymentIntentId,
  };
}

/** Completed appointments → receipts, most-recent first, optional limit. */
export function receiptsFromAppointments(appts: ReceiptAppt[], opts?: { limit?: number }): Receipt[] {
  const receipts = appts.filter(isReceiptAppt).map(buildReceipt).sort((a, b) => b.when - a.when);
  return typeof opts?.limit === 'number' ? receipts.slice(0, opts.limit) : receipts;
}
